import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getApiEndpoint } from '../../../../config/api';
import { FaLock, FaEye, FaEyeSlash, FaEnvelope } from 'react-icons/fa';
import cloudsafeLogo from '../../../../assets/cloudsafe-logo.png';
import './ResetPassword.css';

/** Password-reset page (route: /reset-password/:token). Invites use /join instead. */
const ResetPassword = () => {
  const [pw, setPw] = useState({ password: '', confirmPassword: '' });
  const [show, setShow] = useState({ password: false, confirmPassword: false });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const { token } = useParams();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(getApiEndpoint(`/api/auth/validate-reset-token/${token}`));
        const data = await res.json().catch(() => ({}));
        if (!alive) return;
        if (res.ok && data.valid) {
          // An invite token should use the dedicated join page.
          if (data.purpose === 'invite') { navigate(`/join/${token}`, { replace: true }); return; }
          setEmail(data.email || '');
        } else {
          toast.error(data.message || 'This link is invalid or has expired.');
          navigate('/login');
        }
      } catch {
        if (alive) { toast.error('Could not validate this link.'); navigate('/login'); }
      } finally { if (alive) setChecking(false); }
    })();
    return () => { alive = false; };
  }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.password !== pw.confirmPassword) { toast.error('Passwords do not match'); return; }
    if (pw.password.length < 8) { toast.error('Password must be at least 8 characters long'); return; }
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/auth/reset-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword: pw.password }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || 'Failed to reset password'); }
      toast.success('Password has been reset — you can now sign in.');
      navigate('/login');
    } catch (error) { toast.error(error.message || 'Failed to reset password'); }
    finally { setLoading(false); }
  };

  if (checking) {
    return <div className="rp-page"><div className="rp-card"><p style={{ color: '#6b7280', textAlign: 'center', margin: 0 }}>Validating your link…</p></div></div>;
  }

  return (
    <div className="rp-page">
      <div className="rp-card">
        <div className="rp-brand"><img src={cloudsafeLogo} alt="CloudSafe" className="rp-logo" /></div>

        <h1 className="rp-title">Create a new password</h1>
        <p className="rp-subtitle">Choose a new password for your CloudSafe account.</p>

        <form onSubmit={submit}>
          {email && (
            <div className="rp-group">
              <label className="rp-label">Account</label>
              <div className="rp-input-wrap">
                <FaEnvelope className="rp-input-icon" />
                <input type="email" className="rp-input" value={email} readOnly disabled />
              </div>
            </div>
          )}

          <div className="rp-group">
            <label className="rp-label">New password</label>
            <div className="rp-input-wrap">
              <FaLock className="rp-input-icon" />
              <input type={show.password ? 'text' : 'password'} className="rp-input" placeholder="Enter a new password"
                value={pw.password} onChange={(e) => setPw((p) => ({ ...p, password: e.target.value }))} required autoFocus />
              <button type="button" className="rp-toggle" tabIndex={-1} onClick={() => setShow((s) => ({ ...s, password: !s.password }))}>
                {show.password ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="rp-group">
            <label className="rp-label">Confirm new password</label>
            <div className="rp-input-wrap">
              <FaLock className="rp-input-icon" />
              <input type={show.confirmPassword ? 'text' : 'password'} className="rp-input" placeholder="Re-enter the password"
                value={pw.confirmPassword} onChange={(e) => setPw((p) => ({ ...p, confirmPassword: e.target.value }))} required />
              <button type="button" className="rp-toggle" tabIndex={-1} onClick={() => setShow((s) => ({ ...s, confirmPassword: !s.confirmPassword }))}>
                {show.confirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <p className="rp-hint">Use at least 8 characters. A mix of letters, numbers and a symbol is strongest.</p>

          <button type="submit" className="rp-submit" disabled={loading}>
            {loading ? 'Resetting…' : 'Reset password'}
          </button>
        </form>

        <div className="rp-foot"><span>Remember your password?</span> <Link to="/login" className="rp-link">Back to sign in</Link></div>
      </div>
      <p className="rp-powered">🛡️ Powered by CloudSafe</p>
    </div>
  );
};

export default ResetPassword;
